import { memo, useCallback, useRef, useState } from 'react';
import { Pen, Undo2 } from 'lucide-react';

import { useInput } from '@/utils/hooks';
import Path from 'path-to-regexp';

import {
  uploadFile,
  handleUploadFileOnError,
  getDownloadURL
} from '@/client/media';
import { UploadAvatar } from '@/components/ui/uploadAvatar/uploadAvatar';
import { Input } from '@/shadcn/input';
import { Button } from '@/shadcn/button';
import { GroupPatch, type GroupSettingsQueryQuery } from '@/apollo/graphql';
import { ROUTE_WORKFLOW_GROUPS } from '@/constant/routes';
import { useHistory } from 'react-router';
import { TestId } from '@/types/testId';

type Props = {
  group: GroupSettingsQueryQuery['groupByRowId'];
  updateGroup: (group: GroupPatch) => void;
  updating?: boolean;
};

export const EditableGroupFields = memo(
  ({ group, updateGroup, updating = false }: Props) => {
    const { name, avatar } = group ?? {};

    const history = useHistory();

    // go back to workflow groups page
    const goBack = useCallback(() => {
      history.push(
        Path.compile(ROUTE_WORKFLOW_GROUPS)({
          id: group?.workflow?.rowId
        })
      );
    }, [history, group]);

    const groupNameInputRef = useRef<HTMLInputElement>(null);
    const [inputGroupName, setInputGroupName] = useInput(name);

    const updateGroupName = useCallback(() => {
      if (inputGroupName !== name) updateGroup({ name: inputGroupName });
    }, [updateGroup, name, inputGroupName]);

    const [uploadingAvatar, setUploadingAvatar] = useState(false);

    const updateAvatar = useCallback(
      newAvatarFile => {
        setUploadingAvatar(true);
        uploadFile(
          newAvatarFile,
          async savedFile => {
            const avatarUrl = getDownloadURL(savedFile);
            updateGroup({ avatar: avatarUrl });
            setUploadingAvatar(false);
          },
          () => {
            setUploadingAvatar(false);
            handleUploadFileOnError(
              newAvatarFile.name,
              newAvatarFile.response.status
            );
          },
          null,
          true
        );
      },
      [updateGroup]
    );

    const hasChanged = inputGroupName !== name;

    return (
      <div className="flex items-center gap-3">
        <Button
          variant="ghostSubtle"
          size="icon"
          className="mr-3"
          onClick={goBack}
        >
          <Undo2 />
        </Button>
        <UploadAvatar
          avatar={avatar}
          submitNewAvatar={updateAvatar}
          name={inputGroupName}
          loading={updating || uploadingAvatar}
          data-testid={TestId.EditableGroupFields.UploadAvatar}
        />
        <Input
          type="text"
          ref={groupNameInputRef}
          value={inputGroupName}
          onChange={setInputGroupName}
          data-testid={TestId.EditableGroupFields.GroupNameInput}
          spellCheck="false"
        />
        {hasChanged && (
          <Button
            size="icon"
            className="aspect-square"
            onClick={updateGroupName}
            data-testid={TestId.EditableGroupFields.UpdateGroupNameButton}
          >
            <Pen />
          </Button>
        )}
      </div>
    );
  }
);
